/**
 * ITArchitecture — studio content.
 *
 * All copy, navigation and contact details for the site live here so that
 * nothing textual is buried inside a component. Everything in this file is
 * either taken from the previous version of the site or derived from
 * `projects.js`; no figure is invented.
 *
 * House rule for this file: keep it short. This is an architecture portfolio —
 * the photographs carry the argument, and every extra sentence competes with
 * them. One line beats three.
 *
 * @typedef {Object} NavigationItem
 * @property {string} label    Visible link text (Bosnian).
 * @property {string} href     Route path or in-page anchor.
 * @property {boolean} isRoute True for router navigation, false for an anchor.
 *
 * @typedef {Object} StudioService
 * @property {string} id          Stable slug, used as a React key.
 * @property {string} index       Two-digit ordinal shown in the UI.
 * @property {string} title       Service name.
 * @property {string} description One short line. Not a paragraph.
 */

/** Canonical studio identity, reused by the header, footer and JSON-LD. */
export const studioIdentity = {
  name: "ITArchitecture",
  /*
    The wordmark is set in two weights. The split is after "ITA" — the
    studio's initials are the bold part, with "rchitecture" trailing in a
    light weight: ITArchitecture.
  */
  wordmarkLead: "ITA",
  wordmarkTail: "rchitecture",
  tagline: "Studio za arhitekturu",
  location: "Srebrenik, Bosna i Hercegovina",
  /**
   * Intentionally null: the founding year is not recorded anywhere in the
   * existing site, and the UI omits this block entirely while it is null.
   * Set it to a number (e.g. 2011) to surface "Od <godina>" in the footer.
   */
  foundedYear: null,
};

/** Postal address, telephone and e-mail — carried over unchanged. */
export const contactDetails = {
  emailAddress: "itasmajlovic@gmail.com",
  telephoneDisplay: "+387 61 998 629",
  telephoneHref: "+38761998629",
  streetAddress: "Ulica 21. Srebreničke brigade bb",
  postalCode: "75350",
  city: "Srebrenik",
  country: "Bosna i Hercegovina",
  /** Opens the address in the user's default maps application. */
  mapsUrl:
    "https://www.google.com/maps/search/?api=1&query=" +
    encodeURIComponent(
      "Ulica 21. Srebreničke brigade bb, 75350 Srebrenik, Bosnia and Herzegovina"
    ),
};

/** @type {NavigationItem[]} */
export const primaryNavigation = [
  { label: "Usluge", href: "/#usluge", isRoute: false },
  { label: "Projekti", href: "/projekti", isRoute: true },
  { label: "Kontakt", href: "/#kontakt", isRoute: false },
];

/**
 * The hero statement, split into lines so each can be revealed independently
 * by the masked-line animation.
 */
export const heroContent = {
  headlineLines: ["Prostor", "koji traje."],
  lead: "Od prve skice do predaje ključeva.",
  /*
    Background film: five of the studio's projects, each drawn in pen and then
    filling in, material by material, until it is the studio's own render.

    This replaced the aerial sweep that used to run here. The aerial said the
    studio owns a drone. This says the studio draws buildings and then they
    get built, which is the actual argument.

    How a shot is put together, because it is not obvious from the file:

      - The drawing is a pen version of the render, generated from it and
        checked against it for proportion — bay rhythm, roof overhang,
        column positions.
      - The render underneath is the studio's own file. It is never
        regenerated, so the building cannot drift into one the studio did
        not design.
      - The reveal between them is masked rather than cross-faded, and the
        mask order is read out of the render's own saturation: timber,
        water and planting arrive before neutral concrete and sky. Four
        eased beats, not one dissolve. A plain fade was tried first and read
        as a flip.

    MP4 only, and deliberately. VP9 is about 10% smaller than H.264 on
    photographic footage, which is why the old aerial shipped as WebM first.
    Pen hatching is high-frequency line work and behaves differently: at
    matched quality VP9 came out 2.3 MB against H.264's 2.0 MB, so a WebM
    here would cost bytes to gain nothing.

    720p for the same reason — at 1080p this content is 7 MB, because the
    drawing frames give the encoder no flat areas to work with.

    See public/media/README.md to regenerate.
  */
  videoSources: [{ src: "/media/hero-sketch.mp4", type: "video/mp4" }],
  /** The film's own first frame, so there is no jump when playback starts. */
  posterUrl: "/media/hero-sketch-poster.jpg",
  scrollLabel: "Usluge",
};

/** @type {StudioService[]} — one line each, deliberately. */
export const studioServices = [
  {
    id: "projektovanje",
    index: "01",
    title: "Projektovanje",
    description: "Idejno i izvedbeno rješenje.",
  },
  {
    id: "nadzor",
    index: "02",
    title: "Nadzor",
    description: "Kontrola kvaliteta na gradilištu.",
  },
  {
    id: "izvodjenje",
    index: "03",
    title: "Izvođenje",
    description: "Izvedba s fokusom na detalj.",
  },
  {
    id: "konsalting",
    index: "04",
    title: "Konsalting",
    description: "Savjet o dizajnu, materijalima i rješenjima.",
  },
];

/** Copy for the closing contact section. */
export const contactContent = {
  eyebrow: "Kontakt",
  /* Section ordinals across the home page:
     01 usluge, 02 u fokusu, 03 projekti, 04 kontakt. */
  index: "04",
  headline: "Imate parcelu, ideju ili samo pitanje?",
  lead: "Prvi razgovor je bez obaveze.",
};
